import { Injectable, signal, computed, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, catchError, of } from 'rxjs';
import { User, AuthResponse, LoginDto } from '@shared-types';
import { ConfigService } from './config.service';

const TOKEN_KEY = 'sm_display_token';
const USER_KEY = 'sm_display_user';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly http = inject(HttpClient);
  private readonly router = inject(Router);
  private readonly configService = inject(ConfigService);

  private currentUser = signal<User | null>(this.loadUserFromStorage());
  private token = signal<string | null>(this.loadTokenFromStorage());

  /** Simulierte Rolle für die Vorschau – nur vom echten Admin setzbar. */
  private viewAsRole = signal<string | null>(null);

  /** Effektive Rolle: viewAs überschreibt die echte Rolle. */
  private effectiveRole = computed(() => this.viewAsRole() ?? this.currentUser()?.role ?? null);

  // Computed signals
  readonly user = computed(() => this.currentUser());
  readonly isLoggedIn = computed(() => !!this.token());

  /** Tatsächlicher Admin – wird NIE durch viewAs überschrieben. */
  readonly isRealAdmin = computed(() => this.currentUser()?.role === 'admin');

  /** Aktive Vorschau-Rolle (null = normale Ansicht). */
  readonly isViewingAs = computed(() => this.viewAsRole());

  readonly isAdmin = computed(() => this.effectiveRole() === 'admin');
  /** Manager – breite Betriebsrechte, kein Benutzermanagement */
  readonly isManager = computed(() => this.effectiveRole() === 'manager');
  /** Kunde sieht nur die Upload-Seite (Dashboard/Geräte) */
  readonly isKunde = computed(() => this.effectiveRole() === 'kunde');
  /** Installateur sieht nur das Installateur-Portal */
  readonly isInstaller = computed(() => this.effectiveRole() === 'installer');
  /** Admin, Manager, Vertrieb oder Support – dürfen Kundenverwaltung und Kunden-Medien sehen/bearbeiten */
  readonly canManageCustomers = computed(() => {
    const role = this.effectiveRole();
    return role === 'admin' || role === 'manager' || role === 'sales' || role === 'support';
  });

  /** Admin, Manager oder Vertrieb – Lead-CRM (Support ausgeschlossen) */
  readonly canManageLeads = computed(() => {
    const role = this.effectiveRole();
    return role === 'admin' || role === 'manager' || role === 'sales';
  });

  /** Executive-Dashboard (KPIs + Landkarte) für Manager, Vertrieb, Support und Admin */
  readonly canViewExecutiveDashboard = computed(() => {
    const role = this.effectiveRole();
    return role === 'admin' || role === 'manager' || role === 'sales' || role === 'support';
  });

  /** Nur Admin und Manager – Display-Zuordnungen für Kunden anlegen oder entfernen */
  readonly canAssignCustomerDisplays = computed(() => {
    const role = this.effectiveRole();
    return role === 'admin' || role === 'manager';
  });

  /** Nur Admin und Manager – Bestand anlegen, bearbeiten, zuordnen */
  readonly canManageInventory = computed(() => {
    const role = this.effectiveRole();
    return role === 'admin' || role === 'manager';
  });

  /** Vertragsvorlagen anlegen/bearbeiten (Vertrieb nutzt nur aktive Vorlagen) */
  readonly canEditContractTemplates = computed(() => {
    const role = this.effectiveRole();
    return role === 'admin' || role === 'manager';
  });

  /** Standortpartner-Vertrag stornieren/beenden (Löschen) */
  readonly canRemovePartnerContracts = computed(() => {
    const role = this.effectiveRole();
    return role === 'admin' || role === 'manager';
  });

  /** Sprachassistent (KI-Befehle für Lead / Kunde / Standort-Partner) */
  readonly canUseVoiceAssistant = computed(() => {
    const role = this.effectiveRole();
    return role === 'admin' || role === 'manager';
  });

  /** Browser-Push für Betrieb und Vertrieb */
  readonly canReceivePush = computed(() => {
    const role = this.effectiveRole();
    return role === 'admin' || role === 'manager' || role === 'sales' || role === 'installer';
  });

  private get apiUrl(): string {
    return this.configService.getConfig().apiUrl;
  }

  login(credentials: LoginDto): Observable<AuthResponse> {
    return this.http.post<AuthResponse>(`${this.apiUrl}/auth/login`, credentials).pipe(
      tap((response) => {
        if (!response.twoFactorRequired) {
          this.setSession(response);
        }
      }),
    );
  }

  /** 2FA-Schritt: Temp-Token + TOTP-Code gegen volles JWT tauschen. */
  completeTwoFactorLogin(tempToken: string, code: string): Observable<AuthResponse> {
    return this.http
      .post<AuthResponse>(`${this.apiUrl}/auth/2fa/login`, { tempToken, code })
      .pipe(tap((response) => this.setSession(response)));
  }

  /** Vorschau-Rolle setzen (null = Vorschau beenden). Nur für echte Admins sinnvoll. */
  setViewAs(role: string | null): void {
    this.viewAsRole.set(role);
  }

  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    this.token.set(null);
    this.currentUser.set(null);
    this.viewAsRole.set(null);
    this.router.navigate(['/']);
  }

  getProfile(): Observable<User | null> {
    if (!this.token()) {
      return of(null);
    }

    return this.http.get<User>(`${this.apiUrl}/auth/me`).pipe(
      tap((user) => {
        this.currentUser.set(user);
        localStorage.setItem(USER_KEY, JSON.stringify(user));
      }),
      catchError(() => {
        this.logout();
        return of(null);
      }),
    );
  }

  getToken(): string | null {
    return this.token();
  }

  /** Standard-Route nach Login je nach Rolle */
  getDefaultRoute(): string {
    const role = this.currentUser()?.role;
    if (role === 'kunde') return '/customer';
    if (role === 'installer') return '/installer';
    return '/devices';
  }

  private setSession(authResult: AuthResponse): void {
    if (!authResult.accessToken || !authResult.user) {
      // 2FA-Flow oder ungültige Antwort – Session hier nicht setzen.
      return;
    }
    localStorage.setItem(TOKEN_KEY, authResult.accessToken);
    localStorage.setItem(USER_KEY, JSON.stringify(authResult.user));
    this.token.set(authResult.accessToken);
    this.currentUser.set(authResult.user);
  }

  /** Session nach Passkey- oder 2FA-Login setzen. */
  applyAuthResponse(response: AuthResponse): void {
    this.setSession(response);
  }

  private loadTokenFromStorage(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(TOKEN_KEY);
  }

  private loadUserFromStorage(): User | null {
    if (typeof window === 'undefined') return null;
    const userStr = localStorage.getItem(USER_KEY);
    if (!userStr) return null;
    try {
      return JSON.parse(userStr);
    } catch {
      return null;
    }
  }
}
